import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import EventSeatRoundedIcon from '@mui/icons-material/EventSeatRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

import NEXUSI from '../img/NEXUSI.jpg';
import RAISELABI from '../img/RAISELABI.jpg';
import SANDBOXI from '../img/SANDBOXI.jpg';

const LABS = ['NEXUS', 'RAISE LAB', 'SAND BOX'];
const BUCKET_COUNT = 7;

const LAB_BACKGROUND_MAP = {
  NEXUS: NEXUSI,
  'RAISE LAB': RAISELABI,
  'SAND BOX': SANDBOXI,
};

const getTimestampMs = (value) => {
  if (!value) return null;

  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const formatDuration = (start, end) => {
  const startMs = getTimestampMs(start);
  const endMs = getTimestampMs(end);

  if (!startMs || !endMs || endMs < startMs) return '—';

  const totalMinutes = Math.floor((endMs - startMs) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${minutes} min`;
  if (minutes <= 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

const normalizeString = (value) => (value || '').toString().trim();
const normalizeCompare = (value) => normalizeString(value).toLowerCase();

const buildFullName = (item) => {
  const firstName = normalizeString(item.firstName);
  const lastName = normalizeString(item.lastName);
  return [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown Student';
};

const startOfDayMs = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const startOfWeekMs = (date) => {
  const current = new Date(date);
  const day = current.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  current.setDate(current.getDate() + diff);
  current.setHours(0, 0, 0, 0);
  return current.getTime();
};

const startOfMonthMs = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getTime();
const startOfYearMs = (date) => new Date(date.getFullYear(), 0, 1).getTime();

const addDays = (timestamp, days) => {
  const next = new Date(timestamp);
  next.setDate(next.getDate() + days);
  return next.getTime();
};

const addWeeks = (timestamp, weeks) => addDays(timestamp, weeks * 7);

const addMonths = (timestamp, months) => {
  const next = new Date(timestamp);
  next.setMonth(next.getMonth() + months);
  return next.getTime();
};

const addYears = (timestamp, years) => {
  const next = new Date(timestamp);
  next.setFullYear(next.getFullYear() + years);
  return next.getTime();
};

const formatDayLabel = (timestamp) =>
  new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

const formatWeekLabel = (timestamp) => {
  const start = new Date(timestamp);
  const end = new Date(timestamp);
  end.setDate(start.getDate() + 6);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });

  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}`;
  }

  return `${startMonth} ${start.getDate()}-${endMonth} ${end.getDate()}`;
};

const formatMonthLabel = (timestamp) =>
  new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });

const formatYearLabel = (timestamp) => new Date(timestamp).getFullYear().toString();

const getBucketStart = (timestamp, filter) => {
  const date = new Date(timestamp);

  if (filter === 'day') return startOfDayMs(date);
  if (filter === 'week') return startOfWeekMs(date);
  if (filter === 'month') return startOfMonthMs(date);
  return startOfYearMs(date);
};

const getBucketLabel = (timestamp, filter) => {
  if (filter === 'day') return formatDayLabel(timestamp);
  if (filter === 'week') return formatWeekLabel(timestamp);
  if (filter === 'month') return formatMonthLabel(timestamp);
  return formatYearLabel(timestamp);
};

const getBucketRange = () => BUCKET_COUNT;

const getYAxisTicks = () => [20, 40, 60, 80, 100];

const AttendanceLineChart = ({ data = [] }) => {
  const chartWidth = 1000;
  const chartHeight = 420;
  const padding = { top: 28, right: 18, bottom: 48, left: 42 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const yAxisMax = 100;
  const yTicks = getYAxisTicks();

  const getX = (index) => {
    if (data.length <= 1) return padding.left + innerWidth / 2;
    return padding.left + (index / (data.length - 1)) * innerWidth;
  };

  const getY = (value) => padding.top + innerHeight - (value / yAxisMax) * innerHeight;

  const points = data.map((item, index) => ({
    ...item,
    x: getX(index),
    y: getY(item.percentage || 0),
  }));

  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const areaPath = points.length
    ? [
        `M ${points[0].x} ${padding.top + innerHeight}`,
        ...points.map((point) => `L ${point.x} ${point.y}`),
        `L ${points[points.length - 1].x} ${padding.top + innerHeight}`,
        'Z',
      ].join(' ')
    : '';

  const xTickFontSize = data.length >= 7 ? 13 : 14;
  const pointRadius = data.length >= 7 ? 4.5 : 5.5;

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
      >
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="attendanceAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.26" />
              <stop offset="60%" stopColor="#2563eb" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#130f72" stopOpacity="0.03" />
            </linearGradient>

            <linearGradient id="attendanceLineStroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#60a5fa" />
              <stop offset="55%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#130f72" />
            </linearGradient>

            <filter id="chartShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow
                dx="0"
                dy="8"
                stdDeviation="8"
                floodColor="#2563eb"
                floodOpacity="0.12"
              />
            </filter>
          </defs>

          {yTicks.map((value) => {
            const y = getY(value);

            return (
              <g key={`grid-${value}`}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={chartWidth - padding.right}
                  y2={y}
                  stroke="rgba(148,163,184,0.18)"
                  strokeWidth="1"
                  strokeDasharray="4 6"
                />
                <text
                  x={4}
                  y={y + 4}
                  fontSize="14"
                  fill="#94a3b8"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {value}%
                </text>
              </g>
            );
          })}

          {points.length > 0 ? (
            <>
              <path d={areaPath} fill="url(#attendanceAreaFill)" />
              <path
                d={linePath}
                fill="none"
                stroke="url(#attendanceLineStroke)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#chartShadow)"
              />
            </>
          ) : null}

          {points.map((point, index) => (
            <g key={`${point.key}-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r={pointRadius}
                fill="#ffffff"
                stroke="#2563eb"
                strokeWidth="3"
              />
              <circle cx={point.x} cy={point.y} r="13" fill="transparent">
                <title>{`${point.label}: ${point.percentage.toFixed(
                  1
                )}% average present • ${point.sessions} sessions`}</title>
              </circle>

              <text
                x={point.x}
                y={point.y - 12}
                textAnchor="middle"
                fontSize="13"
                fill={point.percentage > 0 ? '#0f172a' : '#94a3b8'}
                style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
              >
                {`${Math.round(point.percentage)}%`}
              </text>

              <text
                x={point.x}
                y={chartHeight - 12}
                textAnchor="middle"
                fontSize={xTickFontSize}
                fill="#64748b"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {point.label}
              </text>
            </g>
          ))}
        </svg>
      </Box>
    </Box>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    students: 0,
    faculties: 0,
  });
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [labStatuses, setLabStatuses] = useState({
    NEXUS: { status: 'Vacant', session: null },
    'RAISE LAB': { status: 'Vacant', session: null },
    'SAND BOX': { status: 'Vacant', session: null },
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('day');

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const [studentsSnap, facultiesSnap, sessionsSnap] = await Promise.all([
        getDocs(collection(db, 'Students')),
        getDocs(collection(db, 'Faculties')),
        getDocs(collection(db, 'Sessions')),
      ]);

      const studentsData = studentsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const sessionsData = sessionsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const attendanceResults = await Promise.all(
        sessionsSnap.docs.map(async (sessionDoc) => {
          const attendanceSnap = await getDocs(
            collection(db, 'Sessions', sessionDoc.id, 'Attendance')
          );

          return attendanceSnap.docs.map((attendanceDoc) => ({
            id: attendanceDoc.id,
            sessionId: sessionDoc.id,
            ...attendanceDoc.data(),
          }));
        })
      );

      const attendanceData = attendanceResults.flat();

      setCounts({
        students: studentsSnap.size,
        faculties: facultiesSnap.size,
      });

      setStudents(studentsData);
      setSessions(sessionsData);
      setAttendance(attendanceData);

      const nextStatuses = {
        NEXUS: { status: 'Vacant', session: null },
        'RAISE LAB': { status: 'Vacant', session: null },
        'SAND BOX': { status: 'Vacant', session: null },
      };

      sessionsData.forEach((data) => {
        const labName = data?.selectedLab;
        const endTime = data?.endTime;

        if (!LABS.includes(labName)) return;

        if (!endTime) {
          nextStatuses[labName] = {
            status: 'Occupied',
            session: {
              prof: data?.prof || 'Unknown Professor',
              subject: data?.subject || 'No Subject',
              course: data?.course || 'No Course',
              section: data?.section || '',
              yearLevel: data?.yearLevel || '',
              startTime: data?.startTime || null,
            },
          };
        }
      });

      setLabStatuses(nextStatuses);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setErrorMessage('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const attendanceBySession = useMemo(() => {
    const grouped = {};

    attendance.forEach((item) => {
      const sessionId = normalizeString(item.sessionId);
      if (!sessionId) return;

      if (!grouped[sessionId]) grouped[sessionId] = [];
      grouped[sessionId].push(item);
    });

    return grouped;
  }, [attendance]);

  const processedSessions = useMemo(() => {
    return sessions
      .map((session) => {
        const sessionAttendance = attendanceBySession[session.id] || [];

        const studentsPresent = sessionAttendance.map((record) => ({
          id: normalizeString(record.id),
          studentId: normalizeString(record.studentId) || normalizeString(record.id),
          rfid: normalizeString(record.rfid),
          firstName: normalizeString(record.firstName),
          lastName: normalizeString(record.lastName),
          fullName: buildFullName(record),
          timeIn: getTimestampMs(record.timeIn),
          timeOut: getTimestampMs(record.timeOut),
          status: normalizeString(record.status),
        }));

        const startTimeMs = getTimestampMs(session.startTime);

        const enrolledStudents = students.filter((student) => {
          const sameCourse =
            normalizeCompare(student.course) === normalizeCompare(session.course);
          const sameYearLevel =
            normalizeCompare(student.yearLevel) === normalizeCompare(session.yearLevel);
          const sameSection =
            normalizeCompare(student.section) === normalizeCompare(session.section);

          return sameCourse && sameYearLevel && sameSection;
        });

        const enrolledCount = enrolledStudents.length;
        const presentCount = studentsPresent.filter((s) => s.timeIn || s.timeOut).length;
        const completeCount = studentsPresent.filter((s) => s.timeIn && s.timeOut).length;
        const incompleteCount = studentsPresent.filter(
          (s) => (s.timeIn && !s.timeOut) || (!s.timeIn && s.timeOut)
        ).length;
        const lateCount = studentsPresent.filter((s) => s.status === 'Late').length;
        const missingCount = Math.max(enrolledCount - presentCount, 0);
        const attendancePercent =
          enrolledCount > 0 ? Math.min((presentCount / enrolledCount) * 100, 100) : 0;

        return {
          ...session,
          startTimeMs,
          students: studentsPresent,
          totalStudents: presentCount,
          enrolledCount,
          presentCount,
          missingCount,
          lateCount,
          attendancePercent,
          completeCount,
          incompleteCount,
          durationLabel: formatDuration(session.startTime, session.endTime),
        };
      })
      .sort((a, b) => (b.startTimeMs || 0) - (a.startTimeMs || 0));
  }, [sessions, attendanceBySession, students]);

  const summary = useMemo(() => {
    const totalSessions = processedSessions.length;
    const totalLateStudents = processedSessions.reduce((sum, item) => sum + item.lateCount, 0);
    const totalAbsentStudents = processedSessions.reduce((sum, item) => sum + item.missingCount, 0);
    const totalIncomplete = processedSessions.reduce((sum, item) => sum + item.incompleteCount, 0);

    return {
      totalSessions,
      totalLateStudents,
      totalAbsentStudents,
      totalIncomplete,
    };
  }, [processedSessions]);

  const summaryCards = useMemo(
    () => [
      {
        key: 'sessions',
        label: 'Total Sessions',
        value: summary.totalSessions,
        icon: EventNoteRoundedIcon,
        accent: '#0f766e',
        softBg: 'rgba(15, 118, 110, 0.08)',
        description: 'Recorded lab sessions',
      },
      {
        key: 'faculties',
        label: 'Faculties',
        value: counts.faculties,
        icon: GroupsRoundedIcon,
        accent: '#7c3aed',
        softBg: 'rgba(124, 58, 237, 0.08)',
        description: 'Registered faculties',
      },
      {
        key: 'students',
        label: 'Students',
        value: counts.students,
        icon: SchoolRoundedIcon,
        accent: '#2563eb',
        softBg: 'rgba(37, 99, 235, 0.08)',
        description: 'Registered students',
      },
      {
        key: 'incompleteLogs',
        label: 'Incomplete Logs',
        value: summary.totalIncomplete,
        icon: AccessTimeRoundedIcon,
        accent: '#b45309',
        softBg: 'rgba(180, 83, 9, 0.08)',
        description: 'Missing time in or out',
      },
      {
        key: 'lateStudents',
        label: 'Late Students',
        value: summary.totalLateStudents,
        icon: AccessTimeRoundedIcon,
        accent: '#dc2626',
        softBg: 'rgba(220, 38, 38, 0.08)',
        description: 'Students marked late',
      },
      {
        key: 'absentStudents',
        label: 'Absent Students',
        value: summary.totalAbsentStudents,
        icon: PersonRoundedIcon,
        accent: '#c2410c',
        softBg: 'rgba(194, 65, 12, 0.08)',
        description: 'Enrolled but not present',
      },
    ],
    [counts, summary]
  );

  const labCards = useMemo(
    () =>
      LABS.map((labName) => {
        const isOccupied = labStatuses[labName]?.status === 'Occupied';

        return {
          key: labName,
          label: labName,
          status: isOccupied ? 'Occupied' : 'Vacant',
          session: labStatuses[labName]?.session || null,
          accent: isOccupied ? '#f59e0b' : '#22c55e',
          softBg: isOccupied ? 'rgba(245, 158, 11, 0.18)' : 'rgba(34, 197, 94, 0.18)',
          borderColor: isOccupied ? 'rgba(245, 158, 11, 0.30)' : 'rgba(34, 197, 94, 0.26)',
          icon: isOccupied ? SensorsRoundedIcon : EventSeatRoundedIcon,
          backgroundImage: LAB_BACKGROUND_MAP[labName],
        };
      }),
    [labStatuses]
  );

  const attendanceChartData = useMemo(() => {
    const grouped = {};

    processedSessions.forEach((session) => {
      if (!session.startTimeMs) return;
      const bucketStart = getBucketStart(session.startTimeMs, attendanceFilter);

      if (!grouped[bucketStart]) {
        grouped[bucketStart] = {
          key: bucketStart,
          sessions: 0,
          totalPercent: 0,
        };
      }

      grouped[bucketStart].sessions += 1;
      grouped[bucketStart].totalPercent += session.attendancePercent || 0;
    });

    const rangeCount = getBucketRange();
    const now = Date.now();
    const currentBucket = getBucketStart(now, attendanceFilter);
    const bucketList = [];

    for (let i = rangeCount - 1; i >= 0; i -= 1) {
      let bucket = currentBucket;

      if (attendanceFilter === 'day') bucket = addDays(currentBucket, -i);
      if (attendanceFilter === 'week') bucket = addWeeks(currentBucket, -i);
      if (attendanceFilter === 'month') bucket = addMonths(currentBucket, -i);
      if (attendanceFilter === 'year') bucket = addYears(currentBucket, -i);

      bucketList.push(bucket);
    }

    return bucketList.map((bucketStart) => {
      const bucket = grouped[bucketStart];
      const percentage =
        bucket && bucket.sessions > 0 ? bucket.totalPercent / bucket.sessions : 0;

      return {
        key: bucketStart,
        label: getBucketLabel(bucketStart, attendanceFilter),
        percentage,
        sessions: bucket?.sessions || 0,
        isEmpty: !bucket,
      };
    });
  }, [processedSessions, attendanceFilter]);

  const averageChartPercentage = useMemo(() => {
    const withData = attendanceChartData.filter((item) => item.sessions > 0);
    if (!withData.length) return 0;
    return withData.reduce((sum, item) => sum + item.percentage, 0) / withData.length;
  }, [attendanceChartData]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#130f72', mb: 2 }} />
                  <Typography sx={{ color: '#130f72', fontWeight: 700 }}>
                    Loading dashboard...
                  </Typography>
                </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          mx: { xs: 2, md: 5 },
          mt: '33px',
          mb: 5,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 2.2,
            alignItems: 'stretch',
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'minmax(0, 1fr) minmax(380px, 500px)',
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.4 },
              borderRadius: '26px',
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #130f72 0%, #1d4ed8 55%, #2563eb 100%)',
              boxShadow: '0 22px 50px rgba(19, 15, 114, 0.18)',
              overflow: 'hidden',
              position: 'relative',
              minHeight: { xs: 'auto', xl: 278 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -70,
                right: -40,
                width: 220,
                height: 220,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -55,
                left: '42%',
                width: 180,
                height: 180,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
              }}
            />

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pl: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.9375rem', md: '2.3875rem' },
                  fontFamily: 'Space Grotesk, sans-serif',
                  lineHeight: 1.05,
                  color: '#fff',
                  letterSpacing: '-0.03em',
                }}
              >
                Overview
              </Typography>

              <Typography
                sx={{
                  mt: 0.9,
                  width: '100%',
                  maxWidth: { xs: '100%', xl: 620 },
                  color: 'rgba(255,255,255,0.84)',
                  fontSize: '1.0775rem',
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1.7,
                  pr: { xs: 0, xl: 2 },
                }}
              >
                Monitor computer laboratory activity, attendance performance, and real-time
                occupancy in one clean overview.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mt: 1.8, flexWrap: 'wrap' }}
              >
                <Chip
                  label={`${summary.totalSessions} Total Sessions`}
                  sx={{
                    width: 'fit-content',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Poppins, sans-serif',
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    height: 38,
                    fontSize: '0.9675rem',
                  }}
                />
                <Chip
                  label={`${counts.faculties} Faculties`}
                  sx={{
                    width: 'fit-content',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Poppins, sans-serif',
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    height: 38,
                    fontSize: '0.9675rem',
                  }}
                />
                <Chip
                  label={`${counts.students} Students`}
                  sx={{
                    width: 'fit-content',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Poppins, sans-serif',
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    height: 38,
                    fontSize: '0.9675rem',
                  }}
                />
              </Stack>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              height: '100%',
              width: '100%',
              minWidth: 0,
              borderRadius: '24px',
              p: 1.7,
              background: '#ffffff',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', xl: 278 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 0.7,
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    color: '#130f72',
                    fontWeight: 800,
                    fontSize: '1.2175rem',
                    fontFamily: 'Space Grotesk, sans-serif',
                  }}
                >
                  Laboratory Status
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#64748b',
                    fontSize: '0.9575rem',
                    fontFamily: 'Poppins, sans-serif',
                    lineHeight: 1.55,
                  }}
                >
                  Check which labs are occupied right now.
                </Typography>
              </Box>

              <Chip
                icon={<CircleRoundedIcon sx={{ fontSize: '0.8375rem !important' }} />}
                label="Live View"
                sx={{
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  color: '#130f72',
                  fontWeight: 700,
                  fontFamily: 'Poppins, sans-serif',
                  borderRadius: '12px',
                  border: '1px solid #dbeafe',
                  height: 36,
                  fontSize: '0.8975rem',
                  '& .MuiChip-icon': {
                    color: '#22c55e',
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 0.9,
                mt: 0.8,
                alignItems: 'end',
              }}
            >
              {labCards.map((lab) => {
                const LabIcon = lab.icon;

                return (
                  <Paper
                    key={lab.key}
                    elevation={0}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '1 / 1',
                      minWidth: 0,
                      p: 1.05,
                      borderRadius: '16px',
                      border: `1px solid ${lab.borderColor}`,
                      overflow: 'hidden',
                      backgroundImage: `url(${lab.backgroundImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: lab.session
                          ? 'linear-gradient(180deg, rgba(15,23,42,0.28) 0%, rgba(15,23,42,0.62) 55%, rgba(15,23,42,0.82) 100%)'
                          : 'linear-gradient(180deg, rgba(15,23,42,0.36) 0%, rgba(15,23,42,0.68) 55%, rgba(15,23,42,0.84) 100%)',
                      }}
                    />

                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 0.8,
                          mb: 0.8,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '11px',
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            backdropFilter: 'blur(6px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <LabIcon sx={{ color: '#ffffff', fontSize: 19 }} />
                        </Box>

                        <Chip
                          label={lab.status}
                          size="small"
                          sx={{
                            height: 26,
                            backgroundColor: lab.softBg,
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: '0.7775rem',
                            fontFamily: 'Poppins, sans-serif',
                            borderRadius: '8px',
                            backdropFilter: 'blur(6px)',
                            border: '1px solid rgba(255,255,255,0.14)',
                            '& .MuiChip-label': {
                              px: 0.8,
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ mt: 'auto' }}>
                        <Typography
                          sx={{
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: '1.0175rem',
                            fontFamily: 'Space Grotesk, sans-serif',
                            lineHeight: 1.2,
                            textShadow: '0 1px 10px rgba(0,0,0,0.22)',
                          }}
                        >
                          {lab.label}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.45,
                            color: 'rgba(255,255,255,0.88)',
                            fontSize: '0.73rem',
                            fontFamily: 'Poppins, sans-serif',
                            lineHeight: 1.4,
                            minHeight: 36,
                            overflow: 'hidden',
                            textShadow: '0 1px 10px rgba(0,0,0,0.24)',
                          }}
                        >
                          {lab.session
                            ? `${lab.session.subject} • ${lab.session.prof}`
                            : 'No active session right now'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        </Box>

        {errorMessage ? (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              mb: 3,
              p: 2,
              borderRadius: '18px',
              border: '1px solid #fecaca',
              backgroundColor: '#fff1f2',
            }}
          >
            <Typography
              sx={{
                color: '#b91c1c',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                fontSize: '1.0375rem',
              }}
            >
              {errorMessage}
            </Typography>
          </Paper>
        ) : null}

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 2.2,
            alignItems: 'stretch',
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'minmax(0, 1fr) minmax(380px, 500px)',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1.35,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                alignItems: 'stretch',
                height: '100%',
              }}
            >
              {summaryCards.map((card) => {
                const IconComponent = card.icon;

                return (
                  <Paper
                    key={card.key}
                    elevation={0}
                    sx={{
                      width: '100%',
                      minHeight: 178,
                      height: '100%',
                      p: 1.45,
                      borderRadius: '17px',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                      display: 'grid',
                      gridTemplateRows: '42px auto auto 1fr',
                      pl: 2,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 30px rgba(15, 23, 42, 0.10)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '13px',
                        backgroundColor: card.softBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ color: card.accent, fontSize: 22 }} />
                    </Box>

                    <Typography
                      sx={{
                        mt: 1.2,
                        color: '#64748b',
                        fontSize: '0.9975rem',
                        fontFamily: 'Poppins, sans-serif',
                        lineHeight: 1.4,
                      }}
                    >
                      {card.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        color: '#0f172a',
                        fontSize: '1.8875rem',
                        fontWeight: 900,
                        fontFamily: 'Space Grotesk, sans-serif',
                        lineHeight: 1,
                        wordBreak: 'break-word',
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {card.value}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.8,
                        color: '#94a3b8',
                        fontSize: '0.9175rem',
                        fontFamily: 'Poppins, sans-serif',
                        lineHeight: 1.45,
                        alignSelf: 'end',
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              minWidth: 0,
              height: '100%',
              minHeight: { xs: 495, xl: '100%' },
              p: { xs: 2, md: 2.2 },
              borderRadius: '24px',
              background:
                'radial-gradient(circle at top right, rgba(37,99,235,0.08), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ mb: 0.45 }}>
              <Typography
                sx={{
                  color: '#130f72',
                  fontWeight: 800,
                  fontSize: '1.2775rem',
                  fontFamily: 'Space Grotesk, sans-serif',
                  lineHeight: 1.1,
                }}
              >
                Attendance Percentage
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: '#64748b',
                  fontSize: '0.9575rem',
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1.55,
                }}
              >
                View attendance trends by selected period.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.2,
                flexWrap: 'wrap',
                mb: 1.6,
                mt: 1,
              }}
            >
              <ToggleButtonGroup
                exclusive
                value={attendanceFilter}
                onChange={(_, value) => {
                  if (value) setAttendanceFilter(value);
                }}
                size="small"
                sx={{
                  flexWrap: 'wrap',
                  gap: 0.8,
                  '& .MuiToggleButton-root': {
                    border: '1px solid #dbe5f1 !important',
                    borderRadius: '10px !important',
                    px: 1.2,
                    py: 0.55,
                    color: '#475569',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.77rem',
                    textTransform: 'none',
                    backgroundColor: '#fff',
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#130f72 !important',
                    color: '#fff !important',
                    boxShadow: '0 10px 20px rgba(19, 15, 114, 0.18)',
                  },
                }}
              >
                <ToggleButton value="day">Day</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>

              <Chip
                label={`${averageChartPercentage.toFixed(1)}% Avg Present`}
                sx={{
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  color: '#1d4ed8',
                  fontWeight: 700,
                  fontFamily: 'Poppins, sans-serif',
                  borderRadius: '10px',
                  fontSize: '0.77rem',
                  height: 38,
                }}
              />
            </Box>

            <Box
              sx={{
                position: 'relative',
                flex: 1,
                minHeight: { xs: 320, xl: 0 },
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, #f8fbff 100%)',
                px: { xs: 1, md: 1.2 },
                pt: 1.4,
                pb: 1,
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(circle at top right, rgba(37,99,235,0.07), transparent 32%)',
                  pointerEvents: 'none',
                }}
              />

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                }}
              >
                <AttendanceLineChart data={attendanceChartData} />
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;